import {ERROR_MSG, ERRORS_BY_STATUS} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import type {
  APIResult,
  CopilotSpaceId,
  CopilotSpacePayload,
  FailedAPIResult,
  IndexCopilotSpace,
} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {customCopilotApiPath} from '@github-ui/copilot-chat/utils/custom-copilots-helpers'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

const urlPathPrefix = '/github-copilot/chat'

export const COPILOT_SPACES_QUERY_KEY = ['copilot-chat', 'custom-copilots']

export function copilotSpacesQueryKey(id: CopilotSpaceId | null) {
  if (!id) {
    return ['copilot-chat', 'custom-copilot', null]
  }
  return ['copilot-chat', 'custom-copilot', {id: id.id, owner: id.owner}]
}

export interface VisibilitySettings {
  memberCount: number | null
}

export interface MenuActionsData {
  visibility: string
  shareable: boolean
  editable: boolean
  adminable: boolean
  baseRole: string
  public: boolean
}

export async function fetchCopilotSpaces(): Promise<IndexCopilotSpace[]> {
  const res = await verifiedFetchJSON(`${urlPathPrefix}/custom_copilots`)
  if (!res.ok) throw new Error(`Failed to fetch spaces: ${ERRORS_BY_STATUS[res.status] || ERROR_MSG}`)
  const payload = await res.json()
  return payload
}

export async function fetchCopilotSpace(
  customCopilotID: CopilotSpaceId | null,
): Promise<APIResult<CopilotSpacePayload>> {
  // This should never run because we're setting
  // the enabled property to false in the useQuery call that uses it
  if (!customCopilotID || !customCopilotID.owner) {
    return {status: 500, ok: false, error: 'unexpected null custom copilot id or missing owner'}
  }
  const {owner, id} = customCopilotID
  const url = `${urlPathPrefix}/custom_copilots/${owner}/${id}`

  const res = await verifiedFetchJSON(url)
  if (!res.ok) return {status: res.status, ok: false, error: ERRORS_BY_STATUS[res.status] || ERROR_MSG}
  const payload = await res.json()
  return {status: res.status, ok: true, payload}
}

export async function deleteCopilotSpace(copilotId: CopilotSpaceId): Promise<APIResult<null>> {
  const res = await verifiedFetchJSON(customCopilotApiPath(copilotId), {method: 'DELETE'})
  if (!res.ok) return res as unknown as FailedAPIResult

  return {status: res.status, ok: true, payload: null}
}

export async function fetchVisibilitySettings(copilotId: CopilotSpaceId): Promise<VisibilitySettings> {
  const res = await verifiedFetchJSON(`${customCopilotApiPath(copilotId)}/settings/visibility`)
  if (!res.ok) throw new Error(`Error fetching visibility settings`)
  try {
    const data = await res.json()
    return {
      memberCount: data.member_count && typeof data.member_count === 'number' ? (data.member_count as number) : null,
    }
  } catch (error) {
    throw new Error(`Error parsing visibility settings: ${error}`)
  }
}

export async function fetchMenuActions(copilotId: CopilotSpaceId): Promise<MenuActionsData> {
  const res = await verifiedFetchJSON(`${customCopilotApiPath(copilotId)}/menu_actions`)
  if (!res.ok) throw new Error(`Error fetching menu actions`)
  try {
    const data = await res.json()
    return {
      visibility: data.visibility ?? 'private',
      shareable: data.shareable ?? false,
      editable: data.editable ?? false,
      adminable: data.adminable ?? false,
      baseRole: data.baseRole ?? 'none',
      public: data.public ?? false,
    }
  } catch (error) {
    throw new Error(`Error parsing menu actions: ${error}`)
  }
}
